import React, { useState, useRef } from 'react';
import { devLog, devWarn, devError } from '../../../utils/devLogger';
import GenericListUI, { FetchParams, FetchResult } from '../../../components/CustomList/GenericListUI';
import { ColumnDefinition } from '../../../components/CustomList/GenericDataTable';
import { getUserList, UserListParams } from '../../../lib/api/admin';
import UserDetailPopup from './UserDetailPopup';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';

// 유저 데이터 타입 정의 (API 응답과 UI에서 사용할 확장 타입)
interface UserData {
  id?: number; // UI에서 사용할 고유 ID
  no: number;
  index: number;
  name: string | null;
  email: string | null;
  locationType: 'DOMESTIC' | 'INTERNATIONAL';
  cellphone: string;
  createAt: string;
  updateAt: string;
  providerType: string | null;
  providerId: string | null;
  lastLoginAt: string | null; // API에서 lastLoginAt으로 오므로 변경
}

const UserManagementPage: React.FC = () => {
  const [refreshTrigger] = useState(0);
  const listRef = useRef<{ refetch: () => void }>(null);
  
  // 팝업 상태
  const [isDetailPopupOpen, setIsDetailPopupOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);

  // 국가 타입 포맷팅 함수
  const formatLocationType = (locationType: 'DOMESTIC' | 'INTERNATIONAL') => {
    return locationType === 'DOMESTIC' ? '국내' : '국외';
  };

  // 로그인 계정 타입 포맷팅 함수
  const formatProviderType = (providerType: string | null) => {
    if (!providerType) return '일반';
    
    switch (providerType.toUpperCase()) {
      case 'KAKAO':
        return '카카오';
      case 'NAVER':
        return '네이버';
      case 'GOOGLE':
        return '구글';
      case 'APPLE':
        return '애플';
      default:
        return providerType;
    }
  };

  // 컬럼 정의
  const columns: ColumnDefinition<UserData>[] = [
    {
      header: 'No',
      accessor: 'no',
      formatter: (value) => value?.toString() || '',
    },
    {
      header: '가입일',
      accessor: 'createAt' as keyof UserData,
      formatter: (value) => {
        if (!value) return '';
        return dayjs(value).format('YYYY.MM.DD');
      },
      excelFormatter: (value) => {
        if (!value) return '';
        return dayjs(value).format('YYYY.MM.DD HH:mm');
      },
    },
    {
      header: '최근접속',
      accessor: 'lastLoginAt' as keyof UserData,
      formatter: (value) => {
        if (!value) return '-';
        return dayjs(value).format('MM.DD HH:mm');
      },
      excelFormatter: (value) => {
        if (!value) return '-';
        return dayjs(value).format('YYYY.MM.DD HH:mm');
      },
    },
    {
      header: '이름',
      accessor: 'name',
      formatter: (value) => value || '-',
    },
    {
      header: '로그인 계정',
      accessor: 'providerType' as keyof UserData,
      formatter: (value) => formatProviderType(value as string | null),
    },
    {
      header: '아이디',
      accessor: 'providerId' as keyof UserData,
      formatter: (value) => value || '-',
    },
    {
      header: '이메일',
      accessor: 'email',
      formatter: (value) => value || '-',
    },
    {
      header: '국가',
      accessor: 'locationType' as keyof UserData,
      formatter: (value) => formatLocationType(value as 'DOMESTIC' | 'INTERNATIONAL'),
    },
    {
      header: '전화번호',
      accessor: 'cellphone' as keyof UserData,
      formatter: (value) => value || '-',
    },
  ];

  // 실제 API 함수
  const fetchUserData = async (params: FetchParams): Promise<FetchResult<UserData>> => {
    try {
      devLog('fetchUserData API 호출:', params);
      
      // FetchParams를 UserListParams로 변환
      const apiParams: UserListParams = {
        keyword: params.keyword || undefined,
        fromDate: params.fromDate || undefined,
        toDate: params.toDate || undefined,
      };

      const response = await getUserList(apiParams);
      
      // 실제 API 응답 구조에 맞게 처리
      // response.data = { statusCode, message, data: [...], metadata: {...} }
      let responseData = response.data?.data || response.data;
      
      // 배열이 아닌 경우 배열로 변환
      if (!Array.isArray(responseData)) {
        if (responseData?.items && Array.isArray(responseData.items)) {
          responseData = responseData.items;
        } else if (responseData && typeof responseData === 'object') {
          responseData = [responseData];
        } else {
          responseData = [];
        }
      }
      
      // 각 아이템에 id가 없는 경우 생성
      responseData = responseData.map((item: any, index: number) => ({
        ...item,
        id: item.id || item.no || index + 1, // no를 id로 사용하거나 index 기반으로 생성
      }));
      
      // API 응답을 FetchResult 형태로 변환
      const result: FetchResult<UserData> = {
        data: responseData,
        totalItems: response.data?.metadata?.totalCnt || responseData.length,
        allItems: response.data?.metadata?.allCnt || responseData.length,
      };
      
      return result;
      
    } catch (error) {
      toast.error('유저 리스트를 불러오는 중 오류가 발생했습니다.');
      
      // 에러 시 빈 결과 반환
      return {
        data: [],
        totalItems: 0,
        allItems: 0,
      };
    }
  };

  // 행 클릭 핸들러 - 유저 상세 정보 팝업 열기
  const handleRowClick = (item: UserData, index: number) => {
    setSelectedUser(item);
    setIsDetailPopupOpen(true);
  };

  // 팝업 닫기
  const closeDetailPopup = () => {
    setIsDetailPopupOpen(false);
    setSelectedUser(null);
  };

  return (
    <>
      <GenericListUI<UserData>
        ref={listRef}
        title="유저 회원관리"
        columns={columns}
        fetchData={fetchUserData}
        excelFileName="UserList"
        keyExtractor={(item, index) => {
          // id는 이제 항상 존재하지만 안전하게 처리
          return item.id ? item.id.toString() : `user-${index}`;
        }}
        enableSearch={true}
        searchPlaceholder="이름, 이메일, 전화번호로 검색"
        enableDateFilter={true}
        dateRangeOptions={['3개월', '6개월', '1년', '지정']}
        itemsPerPageOptions={[10, 20, 50, 100]}
        themeMode="light"
        onRowClick={handleRowClick}
        refreshTrigger={refreshTrigger}
        // 유저는 추가 버튼 없음 (관리자만 관리자를 추가할 수 있음)
      />

      <UserDetailPopup
        isOpen={isDetailPopupOpen}
        userData={selectedUser}
        onClose={closeDetailPopup}
      />
    </>
  );
};

export default UserManagementPage;
